import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { config } from '../../config';
import './ExportadorReportes.css';

const ExportadorReportes = ({ filtros }) => {
  const [exportando, setExportando] = useState(false);
  const [tipoExportacion, setTipoExportacion] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0, width: 0 });

  const exportar = async (tipo, extension, mimeType) => {
    try {
      setExportando(true);
      setTipoExportacion(tipo);

      const response = await axios.post(
        `${config.apiUrl}/dashboard-avanzado/exportar/${tipo}`,
        { filtros },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          responseType: tipo === 'json' ? 'json' : 'blob'
        }
      );

      // Crear link de descarga
      let blob;
      if (tipo === 'json') {
        blob = new Blob([JSON.stringify(response.data, null, 2)], { type: mimeType });
      } else {
        blob = new Blob([response.data], { type: mimeType });
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const baseName =
        tipo === 'pdf'
          ? `informe_expedientes_${new Date().toISOString().split('T')[0]}`
          : `reporte_expedientes_${new Date().toISOString().split('T')[0]}`;
      link.setAttribute('download', `${baseName}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`Error al exportar a ${tipo.toUpperCase()}:`, error);
      alert(
        tipo === 'pdf'
          ? 'Error al generar el Informe PDF'
          : `Error al generar el archivo ${tipo.toUpperCase()}`
      );
    } finally {
      setExportando(false);
      setTipoExportacion('');
    }
  };

  const exportarExcel = () => {
    setShowDropdown(false);
    exportar('excel', 'xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
  };
  
  const exportarPDF = () => {
    setShowDropdown(false);
    exportar('pdf', 'pdf', 'application/pdf');
  };
  
  const exportarCSV = () => {
    setShowDropdown(false);
    exportar('csv', 'csv', 'text/csv');
  };
  
  const exportarJSON = () => {
    setShowDropdown(false);
    exportar('json', 'json', 'application/json');
  };

  // Debug en montaje
  useEffect(() => {
    console.log('🎬 ExportadorReportes montado');
    return () => console.log('🎬 ExportadorReportes desmontado');
  }, []);

  // Debug de cambios en showDropdown
  useEffect(() => {
    console.log('🔄 showDropdown cambió a:', showDropdown);
  }, [showDropdown]);

  // Actualizar posición del botón cuando se abre el dropdown
  useEffect(() => {
    if (showDropdown && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      console.log('📍 Posición del botón:', {
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        right: rect.right,
        width: rect.width,
        height: rect.height,
        scrollY: window.scrollY,
        scrollX: window.scrollX
      });
      const calculatedTop = rect.bottom + window.scrollY + 5; // 5px de separación
      const calculatedLeft = rect.right - 350 + window.scrollX;
      
      // Asegurar que no se salga de la pantalla
      const viewportWidth = window.innerWidth;
      const finalLeft = Math.max(10, Math.min(calculatedLeft, viewportWidth - 360));
      
      console.log('📐 Posición calculada para dropdown:', {
        top: calculatedTop,
        left: calculatedLeft,
        finalLeft: finalLeft,
        viewportWidth: viewportWidth
      });
      setButtonPosition({
        top: calculatedTop,
        left: calculatedLeft,
        width: rect.width
      });
    }
  }, [showDropdown]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (buttonRef.current && buttonRef.current.contains(event.target)) {
        // Click en el botón, no hacer nada
        return;
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        console.log('👆 Click fuera del dropdown, cerrando');
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      // Pequeño delay para evitar que el click del botón cierre inmediatamente
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const dropdownMenu = showDropdown && buttonPosition.top > 0 && (() => {
    console.log('🎨 Renderizando Portal con posición:', buttonPosition);
    return ReactDOM.createPortal(
      <div 
        ref={dropdownRef}
        className="exportador-dropdown"
        style={{
          top: `${buttonPosition.top}px`,
          left: `${buttonPosition.left}px`
        }}
      >
        <div className="exportador-dropdown-header">
          <h6>
            <i className="fas fa-file-export"></i>
            Formato de Exportación
          </h6>
        </div>
        
        <hr className="exportador-dropdown-divider" />
        
        <ul className="exportador-dropdown-list">
          <li>
            <button
              className="exportador-dropdown-item excel"
              onClick={exportarExcel}
              disabled={exportando}
            >
              <i className="fas fa-file-excel"></i>
              <div className="item-content">
                <span className="item-title">
                  Excel (.xlsx)
                  {exportando && tipoExportacion === 'excel' && (
                    <span className="spinner-border spinner-border-sm"></span>
                  )}
                </span>
                <span className="item-description">
                  Con gráficos y formato profesional
                </span>
              </div>
            </button>
          </li>
          
          <li>
            <button
              className="exportador-dropdown-item pdf"
              onClick={exportarPDF}
              disabled={exportando}
            >
              <i className="fas fa-file-pdf"></i>
              <div className="item-content">
                <span className="item-title">
                  Informe PDF
                  {exportando && tipoExportacion === 'pdf' && (
                    <span className="spinner-border spinner-border-sm"></span>
                  )}
                </span>
                <span className="item-description">
                  Diseño ejecutivo HOMES (HTML + Puppeteer): KPIs, gráficos y alertas
                </span>
              </div>
            </button>
          </li>
          
          <li>
            <hr className="exportador-dropdown-divider" />
          </li>
          
          <li>
            <button
              className="exportador-dropdown-item csv"
              onClick={exportarCSV}
              disabled={exportando}
            >
              <i className="fas fa-file-csv"></i>
              <div className="item-content">
                <span className="item-title">
                  CSV (.csv)
                  {exportando && tipoExportacion === 'csv' && (
                    <span className="spinner-border spinner-border-sm"></span>
                  )}
                </span>
                <span className="item-description">
                  Para Excel o herramientas de análisis
                </span>
              </div>
            </button>
          </li>
          
          <li>
            <button
              className="exportador-dropdown-item json"
              onClick={exportarJSON}
              disabled={exportando}
            >
              <i className="fas fa-file-code"></i>
              <div className="item-content">
                <span className="item-title">
                  JSON (.json)
                  {exportando && tipoExportacion === 'json' && (
                    <span className="spinner-border spinner-border-sm"></span>
                  )}
                </span>
                <span className="item-description">
                  Para integraciones API
                </span>
              </div>
            </button>
          </li>
        </ul>
      </div>,
      document.body
    );
  })();

  return (
    <div className="exportador-reportes">
      <button
        ref={buttonRef}
        type="button"
        className="exportador-btn"
        disabled={exportando}
        onClick={() => {
          console.log('🔍 Click en botón exportar, estado actual:', showDropdown);
          setShowDropdown(!showDropdown);
          console.log('🔍 Nuevo estado:', !showDropdown);
        }}
      >
        <i className="fas fa-download"></i>
        {exportando ? 'Exportando...' : 'Exportar'}
      </button>
      {dropdownMenu}
    </div>
  );
};

export default ExportadorReportes;



